import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Typography, Form, Input, Button, Divider, notification } from 'antd';

import ImageUploader from '@/components/shared/ImageUploader';
import PageTitle from '@/components/shared/PageTitle';
import Wrapper from '@/components/shared/Wrapper';
import { NotificationsTypes } from '@/interfaces/notificationsTypes';
import { useGetUserQuery, useUpdateUserMutation } from '@/services/user';
import styled, { css, StyledProp } from '@/styled';

import PasswordProducer from './Password/PasswordProducer';
import PasswordStudent from './Password/PasswordStudent';

const Profile: React.FC = ({ className }: StyledProp) => {
  const { data: profile, isFetching } = useGetUserQuery();
  const [updateUser, { isLoading }] = useUpdateUserMutation();

  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const [isDirty, setIsDirty] = useState(false);

  const openNotificationWithIcon = useCallback(
    (message: string, type: NotificationsTypes) => {
      api[type]({
        message
      });
    },
    [api]
  );

  const setFormValues = useCallback(
    (name: string, email: string, avatar: string) => {
      form.setFieldsValue({ name, email, avatar });
    },
    [form]
  );

  useEffect(() => {
    if (profile && !isFetching) setFormValues(profile?.name, profile?.email, profile?.avatar);
  }, [profile, setFormValues]);

  const setAvatar = (avatar?: string | undefined) => {
    setIsDirty(true);
    form.setFieldsValue({ avatar });
  };

  const handleChange = () => setIsDirty(true);

  const saveChanges = async (values: any) => {
    try {
      await updateUser({ ...values, type: profile.type, avatar: values.avatar ?? '' });
      openNotificationWithIcon('Perfil atualizado com sucesso', NotificationsTypes.SUCCESS);
      setIsDirty(false);
    } catch (err) {
      openNotificationWithIcon('Ocorreu um erro ao atualizar perfil', NotificationsTypes.ERROR);
    }
  };

  if (isFetching) return null;

  return (
    <Wrapper size='medium' className={className}>
      {contextHolder}

      <PageTitle
        title='Dados do Perfil'
        breadcrumbItems={[{ title: <Link to='/resumo'>Resumo</Link> }, { title: 'Dados do Perfil' }]}
      />
      <Form form={form} layout='vertical' size='large' onFinish={saveChanges} autoComplete='off'>
        <div className='profile-container'>
          <div className='profile-avatarContainer'>
            <div className='profile-avatar'>
              <Form.Item name='avatar'>
                <ImageUploader
                  width={175}
                  height={175}
                  preview={false}
                  imageUrl={profile?.avatar}
                  updateAvatar={setAvatar}
                  notification={openNotificationWithIcon}
                />
              </Form.Item>
            </div>

            <div className='profile-avatarInfo'>
              <Form.Item
                label='Nome'
                name='name'
                rules={[{ required: true, message: 'Campo de nome de usuário é obrigatório' }]}
              >
                <Input placeholder='Nome' onChange={handleChange} />
              </Form.Item>
              <Form.Item label='E-mail' name='email'>
                <Input placeholder='E-mail' disabled={true} />
              </Form.Item>
              <Form.Item>
                <Button
                  type='primary'
                  disabled={!isDirty}
                  loading={isLoading}
                  size='large'
                  className='profile-btnSaveChanges'
                  htmlType='submit'
                >
                  Salvar Alterações
                </Button>
              </Form.Item>
            </div>
          </div>
          <Divider type='horizontal' className='profile-divider' />
        </div>
      </Form>
      <div className='profile-changePasswordContainer'>
        <Typography.Title className='profile-subtitle' level={4}>
          Alterar Senha
        </Typography.Title>
        {profile?.type === 1 ? (
          <PasswordProducer />
        ) : (
          <PasswordStudent
            name={profile.name}
            email={profile.email}
            type={profile.type}
            avatar={profile?.avatar}
            notification={openNotificationWithIcon}
          />
        )}
      </div>
    </Wrapper>
  );
};

export default styled(Profile)(
  ({ theme }) => css`
    .profile-container {
      margin-top: 24px;
    }

    .profile-title {
      padding-bottom: ${theme.space.spacing(8)};
    }

    .profile-avatarContainer {
      display: flex;
      justify-content: left;
      align-items: center;

      ${theme.breakpoints.down('md')} {
        display: block;
      }
    }

    .profile-avatar {
      max-width: 250px;
      max-height: 250px;
    }

    .profile-avatarInfo {
      padding-left: ${theme.space.spacing(8)};
      width: 100%;

      ${theme.breakpoints.down('md')} {
        padding-left: 0;
      }
    }

    [title='Nome'],
    [title='E-mail'] {
      color: ${theme.colorTextSecondary} !important;
    }

    .profile-divider {
      border-color: ${theme.colorTextQuaternary};
    }

    .profile-btnSaveChanges {
      &[disabled] {
        border: 0;
      }

      ${theme.breakpoints.down('md')} {
        width: 100%;
      }
    }

    .ant-form-large .ant-form-item .ant-form-item-label > label {
      height: auto;
    }
  `
);
